import { type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { HealedHeartMark } from '@/components/ui/healed-heart-mark';
import {
  MANUAL_SANCTION_REASONS,
  SANCTION_REASON_LABEL,
  type SanctionReason,
} from '@/types';

const REASON_KEYS = MANUAL_SANCTION_REASONS;

const REASON_ROW =
  'flex cursor-pointer items-start gap-sm rounded-md border border-white/[0.04] bg-surface-elevated px-sm py-xs text-xs text-text-secondary hover:border-error/40';

const TEXTAREA_CLS =
  'w-full rounded-md border border-white/[0.06] bg-surface-elevated px-sm py-xs text-xs text-text-primary placeholder:text-text-tertiary/50 transition-colors focus:border-error focus:outline-none focus:ring-2 focus:ring-error/30';

type WarnProps = { username: string | undefined; redeemed: boolean };
type RowProps = {
  m: SanctionReason;
  checked: boolean;
  onSelect: (v: SanctionReason) => void;
};
type PickerProps = {
  value: SanctionReason | '';
  onChange: (v: SanctionReason) => void;
};
type FieldProps = { value: string; onChange: (v: string) => void };
type ActionProps = {
  onCancel: () => void;
  onConfirm: () => void;
  disabled: boolean;
};

export function SanctionWarning({ username, redeemed }: WarnProps): ReactNode {
  return (
    <p className="text-xs text-text-secondary leading-relaxed">
      Esto marcará al creador como inválido y aplicará una sanción a{' '}
      {username ? (
        <>
          @{username}
          {redeemed && <HealedHeartMark />}
        </>
      ) : (
        'el collector que lo trajo'
      )}
      . La sanción es permanente.
    </p>
  );
}

function ReasonRow({ m, checked, onSelect }: RowProps): ReactNode {
  return (
    <label className={REASON_ROW}>
      <input
        type="radio"
        name="sanction-reason"
        checked={checked}
        onChange={() => onSelect(m)}
        className="mt-0.5 accent-error"
      />
      <span>{SANCTION_REASON_LABEL[m]}</span>
    </label>
  );
}

export function ReasonPicker({ value, onChange }: PickerProps): ReactNode {
  return (
    <div className="space-y-1">
      {REASON_KEYS.map((m) => (
        <ReasonRow key={m} m={m} checked={value === m} onSelect={onChange} />
      ))}
    </div>
  );
}

export function NotesField({ value, onChange }: FieldProps): ReactNode {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Notas (opcional)"
      rows={2}
      className={TEXTAREA_CLS}
    />
  );
}

export function SanctionActions({
  onCancel,
  onConfirm,
  disabled,
}: ActionProps): ReactNode {
  return (
    <div className="flex justify-end gap-sm">
      <Button variant="ghost" size="sm" onClick={onCancel}>
        Cancelar
      </Button>
      <Button
        variant="danger"
        size="sm"
        onClick={onConfirm}
        disabled={disabled}
      >
        Sancionar
      </Button>
    </div>
  );
}
